import { useNavigate } from "react-router-dom";
import { LogOut, MoreVertical, Settings } from "lucide-react";
import { useContext } from "react";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { AuthContext } from "modules/authentication/context/auth-provider";
import { UserData } from "modules/authentication/types/user-data";

const getUserData = (): UserData => {
  try {
    const data = localStorage.getItem("userData");
    if (!data) return {} as UserData;
    return JSON.parse(data) as UserData;
  } catch {
    return {} as UserData;
  }
};

const getInitials = (userData: UserData) => {
  const first = userData.first_name?.[0] || "U";
  const last = userData.last_name?.[0] || "S";
  return `${first}${last}`.toUpperCase();
};

const getFullName = (userData: UserData) => {
  const first = userData.first_name || "Usuario";
  const last = userData.last_name || "";
  return `${first} ${last}`.trim();
};

export function NavUser() {
  const { isMobile, setOpenMobile } = useSidebar();
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const userData = getUserData();

  const handleLogout = async () => {
    setOpenMobile(false);
    await logout();
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={userData.avatar_url} alt={getFullName(userData)} />
                <AvatarFallback className="rounded-lg">{getInitials(userData)}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{getFullName(userData)}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {userData.username || "Sin email"}
                </span>
              </div>
              <MoreVertical className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={userData.avatar_url} alt={getFullName(userData)} />
                  <AvatarFallback className="rounded-lg">{getInitials(userData)}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{getFullName(userData)}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {userData.username || "Sin email"}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => { setOpenMobile(false); navigate("/settings"); }}>
                <Settings className="h-4 w-4" />
                Cuenta
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
              Salir
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
